// src/features/landing/presentation/pages/JobSeekerApplyPage.jsx
import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Footer } from "../components/Footer";
import { jobSeekerRequestRepository } from "../../infrastructure/jobSeekerRequestApiRepository";
import { termsRepository } from "../../infrastructure/termsApiRepository";
import { jobCategoryRepository } from "../../infrastructure/jobCategoryApiRepository";
import { useAuthContext } from "../../../auth/application/AuthContext";
import { PlannerCareerFields } from "../components/CareerHistorySection";

import "../styles/landing.css";

// 설계사 채용관 인재등록(/premium/apply/planner)은 희망직종이 보험 설계사로 고정된다.
const PLANNER_CATEGORY_CODE = "INSURANCE_PLANNER";

const EXPERIENCE_OPTIONS = [
    { value: "신입", label: "신입" },
    { value: "1년미만", label: "1년 미만" },
    { value: "1~3년", label: "1 ~ 3년" },
    { value: "3~5년", label: "3 ~ 5년" },
    { value: "5~10년", label: "5 ~ 10년" },
    { value: "10년이상", label: "10년 이상" },
];

const EMPLOYMENT_TYPE_OPTIONS = [
    { value: "정규직", label: "정규직" },
    { value: "계약직", label: "계약직" },
    { value: "인턴", label: "인턴" },
    { value: "프리랜서", label: "프리랜서" },
    { value: "기타", label: "기타" },
];

const EMPTY_CAREER = {
    companyName: "",
    startDate: "",
    endDate: "",
    isCurrent: false,
    employmentType: "",
    jobRole: "",
    position: "",
    mainDuties: "",
};

const EMPTY_PLANNER_CAREER = {
    type: "PLANNER",
    companyName: "",
    salesTypes: [],
    salesTypesEtc: "",
    startDate: "",
    endDate: "",
    isCurrent: false,
    mainDuties: "",
};


// ── 커스텀 셀렉트 ────────────────────────────────────────────────────────────

function SelectDropdown({ name, value, onChange, options, placeholder = "선택하세요" }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    const selected = options.find((o) => o.value === value);

    // 외부 클릭 시 닫기
    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const handleSelect = (optValue) => {
        onChange({ target: { name, value: optValue } });
        setOpen(false);
    };

    return (
        <div className="jsa-custom-select" ref={ref}>
            <button
                type="button"
                className={`jsa-custom-select-trigger ${open ? "open" : ""} ${value ? "has-value" : ""}`}
                onClick={() => setOpen((v) => !v)}
            >
                <span>{selected ? selected.label : placeholder}</span>
                <svg className={`jsa-select-arrow ${open ? "open" : ""}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>

            {open && (
                <ul className="jsa-custom-select-list">
                    {options.map((opt) => (
                        <li
                            key={opt.value}
                            className={`jsa-custom-select-item ${opt.value === value ? "selected" : ""}`}
                            onClick={() => handleSelect(opt.value)}
                        >
                            {opt.value === value && (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                            )}
                            {opt.label}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function BirthDateSelect({ value, onChange, options, placeholder }) {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    const listRef = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    useEffect(() => {
        if (open && listRef.current && value) {
            const selected = listRef.current.querySelector(".bds-item--selected");
            if (selected) selected.scrollIntoView({ block: "nearest" });
        }
    }, [open, value]);

    const selected = options.find((o) => Number(o.value) === Number(value));

    return (
        <div className="bds-wrap" ref={wrapRef}>
            <button
                type="button"
                className={`bds-trigger ${open ? "bds-trigger--open" : ""}`}
                onClick={() => setOpen((v) => !v)}
            >
                <span className={selected ? "" : "bds-placeholder"}>
                    {selected ? selected.label : placeholder}
                </span>
                <span className="bds-arrow">▼</span>
            </button>
            {open && (
                <ul className="bds-list" ref={listRef}>
                    {options.map((o) => (
                        <li
                            key={o.value}
                            className={`bds-item ${Number(o.value) === Number(value) ? "bds-item--selected" : ""}`}
                            onClick={() => { onChange(String(o.value)); setOpen(false); }}
                        >
                            {o.label}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

const INITIAL_FORM = {
    name: "",
    gender: "",
    phone: "",
    email: "",
    jobExperienceLevel: "",
    zipcode: "",
    roadAddress: "",
    detailAddress: "",
    jobCategoryCodes: [],
    privacyAgreed: false,
};

export function JobSeekerApplyPage({ planner = false }) {
    const { accessToken } = useAuthContext();
    const location = useLocation();
    const navigate = useNavigate();
    const editMode = location.state?.editMode === true;
    const editPostId = location.state?.postId;
    const editInitialData = location.state?.initialData;
    // 설계사 채용관 진입(planner)이면 희망직종을 INSURANCE_PLANNER로 고정한다.
    const fixedCategoryCode = planner
        ? PLANNER_CATEGORY_CODE
        : (location.state?.fixedCategoryCode || null);

    const initialForm = fixedCategoryCode
        ? { ...INITIAL_FORM, jobCategoryCodes: [fixedCategoryCode] }
        : INITIAL_FORM;
    const [form, setForm] = useState(initialForm);
    const [resumeFile, setResumeFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingStep, setLoadingStep] = useState(null); // 'uploading' | 'submitting'
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const [jobCategoryOptions, setJobCategoryOptions] = useState([]);

    const [privacyTerms, setPrivacyTerms] = useState(null);
    const [showTermsModal, setShowTermsModal] = useState(false);

    const [emailLocal, setEmailLocal] = useState("");
    const [emailDomain, setEmailDomain] = useState("");
    const [emailDomainCustom, setEmailDomainCustom] = useState("");

    const [visualImgLoaded, setVisualImgLoaded] = useState(false);
    const [birthYear, setBirthYear] = useState("");
    const [birthMonth, setBirthMonth] = useState("");
    const [birthDay, setBirthDay] = useState("");
    const birthDate = birthYear && birthMonth && birthDay
        ? `${birthYear}-${String(birthMonth).padStart(2, "0")}-${String(birthDay).padStart(2, "0")}`
        : "";
    const [careerHistories, setCareerHistories] = useState([]);

    const addCareer = () => setCareerHistories((prev) => [
        ...prev,
        planner ? { ...EMPTY_PLANNER_CAREER } : { ...EMPTY_CAREER },
    ]);

    const updateCareer = (index, field, value) => {
        setCareerHistories((prev) =>
            prev.map((item, i) => {
                if (i !== index) return item;
                const updated = { ...item, [field]: value };
                if (field === "isCurrent" && value) updated.endDate = "";
                return updated;
            })
        );
    };

    const removeCareer = (index) => {
        setCareerHistories((prev) => prev.filter((_, i) => i !== index));
    };

    useEffect(() => {
        jobCategoryRepository.getJobCategories()
            .then((data) => setJobCategoryOptions(data.map((c) => ({ value: c.code, label: c.name }))))
            .catch(() => {});
        termsRepository.getTermsByType("JOB_SEEKER_PRIVACY")
            .then(setPrivacyTerms)
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (!editMode || !editInitialData) return;
        const d = editInitialData;

        // birthDate: "YYYY-MM-DD" → year/month/day
        const [by, bm, bd] = (d.birthDate || '').split('-');
        setBirthYear(by || '');
        setBirthMonth(bm ? String(Number(bm)) : '');
        setBirthDay(bd ? String(Number(bd)) : '');

        // phone: stored without dashes → format back
        const raw = (d.phone || '').replace(/-/g, '');
        let formattedPhone = raw;
        if (raw.length === 11) formattedPhone = `${raw.slice(0,3)}-${raw.slice(3,7)}-${raw.slice(7)}`;
        else if (raw.length === 10) formattedPhone = `${raw.slice(0,3)}-${raw.slice(3,6)}-${raw.slice(6)}`;

        // email: split into local + domain
        const atIdx = (d.email || '').indexOf('@');
        const local = atIdx > -1 ? d.email.slice(0, atIdx) : '';
        const domain = atIdx > -1 ? d.email.slice(atIdx + 1) : '';
        const knownDomains = ['naver.com', 'gmail.com', 'daum.net', 'kakao.com', 'hanmail.net', 'nate.com'];
        setEmailLocal(local);
        if (domain && knownDomains.includes(domain)) {
            setEmailDomain(domain);
        } else if (domain) {
            setEmailDomain('custom');
            setEmailDomainCustom(domain);
        }

        // careerHistories
        try { setCareerHistories(JSON.parse(d.careerHistories || '[]')); } catch { setCareerHistories([]); }

        setForm({
            name: d.name || '',
            gender: d.gender || '',
            phone: formattedPhone,
            email: d.email || '',
            jobExperienceLevel: d.jobExperienceLevel || '',
            zipcode: '',
            roadAddress: d.address || '',
            detailAddress: d.addressDetail || '',
            jobCategoryCodes: d.jobCategoryCodes || [],
            privacyAgreed: true,
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── 입력 핸들러 ──────────────────────────────────────────────────────────

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === "phone") {
            const raw = value.replace(/\D/g, "");
            let formatted;
            if (raw.startsWith("02")) {
                const d = raw.slice(0, 10);
                if (d.length <= 2) formatted = d;
                else if (d.length <= 5) formatted = `${d.slice(0,2)}-${d.slice(2)}`;
                else if (d.length <= 9) formatted = `${d.slice(0,2)}-${d.slice(2,5)}-${d.slice(5)}`;
                else formatted = `${d.slice(0,2)}-${d.slice(2,6)}-${d.slice(6)}`;
            } else if (/^1[0-9]/.test(raw)) {
                const d = raw.slice(0, 8);
                formatted = d.length <= 4 ? d : `${d.slice(0,4)}-${d.slice(4)}`;
            } else {
                const d = raw.slice(0, 11);
                if (d.length <= 3) formatted = d;
                else if (d.length <= 6) formatted = `${d.slice(0,3)}-${d.slice(3)}`;
                else if (d.length <= 10) formatted = `${d.slice(0,3)}-${d.slice(3,6)}-${d.slice(6)}`;
                else formatted = `${d.slice(0,3)}-${d.slice(3,7)}-${d.slice(7)}`;
            }
            setForm((prev) => ({ ...prev, phone: formatted }));
            return;
        }
        setForm((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const EMAIL_DOMAINS = [
        { value: "naver.com", label: "naver.com" },
        { value: "gmail.com", label: "gmail.com" },
        { value: "daum.net", label: "daum.net" },
        { value: "kakao.com", label: "kakao.com" },
        { value: "hanmail.net", label: "hanmail.net" },
        { value: "nate.com", label: "nate.com" },
        { value: "custom", label: "직접입력" },
    ];

    const handleEmailLocalChange = (e) => {
        const local = e.target.value;
        setEmailLocal(local);
        const domain = emailDomain === "custom" ? emailDomainCustom : emailDomain;
        setForm((prev) => ({ ...prev, email: local && domain ? `${local}@${domain}` : "" }));
    };

    const handleEmailDomainSelect = (e) => {
        const domain = e.target.value;
        setEmailDomain(domain);
        if (domain === "custom") {
            setForm((prev) => ({ ...prev, email: "" }));
        } else {
            setForm((prev) => ({ ...prev, email: emailLocal && domain ? `${emailLocal}@${domain}` : "" }));
        }
    };

    const handleEmailDomainCustomChange = (e) => {
        const custom = e.target.value;
        setEmailDomainCustom(custom);
        setForm((prev) => ({ ...prev, email: emailLocal && custom ? `${emailLocal}@${custom}` : "" }));
    };


    const handleJobCategoryChange = (value) => {
        setForm((prev) => {
            const exists = prev.jobCategoryCodes.includes(value);
            return {
                ...prev,
                jobCategoryCodes: exists
                    ? prev.jobCategoryCodes.filter((v) => v !== value)
                    : [...prev.jobCategoryCodes, value],
            };
        });
    };

    const handleResumeChange = (e) => {
        const file = e.target.files[0];
        if (file) setResumeFile(file);
    };

    // ── 다음 주소 검색 ────────────────────────────────────────────────────────

    const handleAddressSearch = () => {
        const open = () => {
            new window.daum.Postcode({
                oncomplete: (data) => {
                    setForm((prev) => ({
                        ...prev,
                        zipcode: data.zonecode,
                        roadAddress: data.roadAddress,
                    }));
                },
            }).open();
        };

        if (!window.daum?.Postcode) {
            const script = document.createElement("script");
            script.src = "//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js";
            script.onload = open;
            document.head.appendChild(script);
        } else {
            open();
        }
    };

    // ── 유효성 검사 ───────────────────────────────────────────────────────────

    const validate = () => {
        if (!form.name.trim()) return "이름을 입력해주세요.";
        if (!form.gender) return "성별을 선택해주세요.";
        if (!form.phone.trim()) return "연락처를 입력해주세요.";
        if (!/^(02-\d{3,4}-\d{4}|0\d{1,2}-\d{3,4}-\d{4}|1[0-9]{3}-\d{4})$/.test(form.phone)) return "연락처 형식이 올바르지 않습니다. (예: 010-1234-5678)";
        if (!form.email.trim()) return "이메일을 입력해주세요.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return "이메일 형식이 올바르지 않습니다.";
        if (!birthDate) return "생년월일을 입력해주세요.";
        if (!form.jobExperienceLevel) return "경력을 선택해주세요.";
        if (!form.roadAddress) return "주소를 검색해주세요.";
        if (form.jobCategoryCodes.length === 0) return "희망직종을 하나 이상 선택해주세요.";
        if (!form.privacyAgreed) return "개인정보 수집·이용에 동의해주세요.";
        return null;
    };

    // ── 제출 ─────────────────────────────────────────────────────────────────

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationError = validate();
        if (validationError) { setError(validationError); return; }

        setLoading(true);
        setError(null);

        try {
            let files;

            if (resumeFile) {
                setLoadingStep('uploading');
                const { fileKey } = await jobSeekerRequestRepository.uploadFile(resumeFile, 'RESUME');
                files = [{ fileType: 'RESUME', fileKey, originalFileName: resumeFile.name }];
            }

            setLoadingStep('submitting');
            // 설계사 경력 항목은 type 마커 부여 + 기타 미선택 시 salesTypesEtc 제거
            const serializedCareers = planner
                ? careerHistories.map((c) => ({
                    type: "PLANNER",
                    companyName: c.companyName,
                    salesTypes: c.salesTypes || [],
                    salesTypesEtc: (c.salesTypes || []).includes("ETC")
                        ? ((c.salesTypesEtc || "").trim() || undefined)
                        : undefined,
                    startDate: c.startDate || undefined,
                    endDate: c.isCurrent ? undefined : (c.endDate || undefined),
                    isCurrent: !!c.isCurrent,
                    mainDuties: (c.mainDuties || "").trim() || undefined,
                }))
                : careerHistories;
            const payload = {
                name: form.name.trim(),
                gender: form.gender,
                phone: form.phone.replace(/-/g, ""),
                birthDate: birthDate,
                email: form.email.trim() || undefined,
                jobExperienceLevel: form.jobExperienceLevel,
                address: form.roadAddress,
                addressDetail: form.detailAddress.trim() || undefined,
                // 설계사 인재등록은 항상 INSURANCE_PLANNER 단일, 일반은 INSURANCE_PLANNER 제외
                // (레거시 혼합 구직글을 수정·재저장할 때도 이 규칙으로 정규화된다)
                jobCategoryCodes: planner
                    ? [PLANNER_CATEGORY_CODE]
                    : form.jobCategoryCodes.filter((c) => c !== PLANNER_CATEGORY_CODE),
                careerHistories: serializedCareers.length > 0 ? JSON.stringify(serializedCareers) : undefined,
                files,
            };
            if (editMode) {
                await jobSeekerRequestRepository.update(editPostId, payload, accessToken);
                navigate('/account?tab=my-seeker-posts', { replace: true });
            } else {
                await jobSeekerRequestRepository.createJobSeekerRequest(payload, accessToken);
                setSuccess(true);
                window.scrollTo({ top: 0, behavior: "smooth" });
            }
        } catch (err) {
            setError(err.message || "신청에 실패했습니다. 잠시 후 다시 시도해주세요.");
        } finally {
            setLoading(false);
            setLoadingStep(null);
        }
    };

    const jobLabels = form.jobCategoryCodes
        .map((v) => jobCategoryOptions.find((o) => o.value === v)?.label)
        .filter(Boolean)
        .join(", ");

    // ── 메인 렌더 ─────────────────────────────────────────────────────────────

    return (
        <>

            <div className="jsa-page">

                {/* 페이지 헤더 */}
                <div className="jsa-hero">
                    <div className="jsa-hero-inner">
                        <p className="jsa-hero-kicker">{planner ? "PREMIUM · 설계사 채용관" : "PREMIUM"}</p>
                        <h1 className="jsa-hero-title">{planner ? "보험 설계사 인재등록" : "인재등록"}</h1>
                        <p className="jsa-hero-sub">라이징브로커즈에서 나에게 맞는 맞춤 채용을 경험하세요.</p>
                    </div>
                </div>

                {/* 본문 */}
                <div className="jsa-body">
                  {success ? (
                    <div className="jsa-success-card">

                        {/* 아이콘 */}
                        <div className="jsa-success-badge">
                            <div className="jsa-success-badge-ring" />
                            <svg className="jsa-success-svg" viewBox="0 0 52 52" fill="none">
                                <circle className="jsa-success-circle" cx="26" cy="26" r="23" stroke="#f16102" strokeWidth="2" fill="none" />
                                <polyline className="jsa-success-check" points="14,27 22,35 38,18" stroke="#f16102" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                            </svg>
                        </div>

                        {/* 제목 */}
                        <div className="jsa-success-header">
                            <h2 className="jsa-success-title">
                                {form.name ? <>{form.name}님, 신청이 완료되었습니다!</> : "신청이 완료되었습니다!"}
                            </h2>
                            <p className="jsa-success-desc">
                                구직 신청서가 성공적으로 접수되었습니다.<br />
                                담당자 검토 후 빠른 시일 내에 연락드리겠습니다.
                            </p>
                        </div>

                        {/* 접수 요약 */}
                        <div className="jsa-success-summary">
                            <p className="jsa-summary-title">접수 정보</p>
                            <ul className="jsa-summary-list">
                                {form.name && (
                                    <li className="jsa-summary-item">
                                        <span className="jsa-summary-key">이름</span>
                                        <span className="jsa-summary-val">{form.name}</span>
                                    </li>
                                )}
                                {form.phone && (
                                    <li className="jsa-summary-item">
                                        <span className="jsa-summary-key">연락처</span>
                                        <span className="jsa-summary-val">{form.phone}</span>
                                    </li>
                                )}
                                {jobLabels && (
                                    <li className="jsa-summary-item">
                                        <span className="jsa-summary-key">희망직종</span>
                                        <span className="jsa-summary-val">{jobLabels}</span>
                                    </li>
                                )}
                                {form.roadAddress && (
                                    <li className="jsa-summary-item">
                                        <span className="jsa-summary-key">주소</span>
                                        <span className="jsa-summary-val">{form.roadAddress}</span>
                                    </li>
                                )}
                            </ul>
                        </div>

                        {/* 진행 단계 */}
                        <div className="jsa-success-steps">
                            <div className="jsa-step jsa-step--done">
                                <div className="jsa-step-circle">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                </div>
                                <span className="jsa-step-label">신청 접수</span>
                            </div>
                            <div className="jsa-step-line" />
                            <div className="jsa-step jsa-step--pending">
                                <div className="jsa-step-circle">2</div>
                                <span className="jsa-step-label">담당자 검토</span>
                            </div>
                            <div className="jsa-step-line" />
                            <div className="jsa-step jsa-step--pending">
                                <div className="jsa-step-circle">3</div>
                                <span className="jsa-step-label">연락 안내</span>
                            </div>
                        </div>

                        {/* 안내 */}
                        <div className="jsa-success-notice">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#f16102" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="8" x2="12" y2="12" />
                                <line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                            <span>등록하신 연락처로 담당자가 직접 연락드립니다. 전화 받기 어려운 시간대가 있으시면 이메일로 미리 알려주세요.</span>
                        </div>

                        {/* 버튼 */}
                        <div className="jsa-success-actions">
                            <a href="/jobs" className="jsa-success-home-btn">홈으로 가기</a>
                            <button
                                className="jsa-submit-btn jsa-success-retry-btn"
                                onClick={() => { setSuccess(false); setForm(initialForm); setCareerHistories([]); setResumeFile(null); }}
                            >
                                다시 신청하기
                            </button>
                        </div>
                    </div>
                  ) : (
                    <div className="jsa-layout">

                        {/* 왼쪽: 이미지 */}
                        <div className="jsa-image-col">
                            <img
                                src="/images/premium/apply-visual.jpg"
                                alt="구직 신청"
                                className="jsa-visual-img"
                                style={{ display: visualImgLoaded ? "block" : "none" }}
                                onLoad={() => setVisualImgLoaded(true)}
                                onError={() => setVisualImgLoaded(false)}
                            />
                            <div className="jsa-image-badge">
                                <span className="jsa-badge-num">라이징브로커즈</span>
                                <span className="jsa-badge-txt">프리미엄 채용 플랫폼</span>
                            </div>
                        </div>

                        {/* 오른쪽: 폼 */}
                        <div className="jsa-form-col">
                            <form className="jsa-form" onSubmit={handleSubmit} noValidate>

                                {/* 기본 정보 */}
                                <div className="jsa-section-title">기본 정보</div>

                                <div className="jsa-row-2">
                                    <div className="jsa-field">
                                        <label className="jsa-label">이름 <span className="jsa-required">*</span></label>
                                        <input className="jsa-input" type="text" name="name" value={form.name} onChange={handleChange} placeholder="이름을 입력하세요" />
                                    </div>
                                    <div className="jsa-field">
                                        <label className="jsa-label">연락처 <span className="jsa-required">*</span></label>
                                        <input className="jsa-input" type="tel" name="phone" value={form.phone} onChange={handleChange} placeholder="010-0000-0000" />
                                    </div>
                                </div>

                                <div className="jsa-row-2">
                                    <div className="jsa-field">
                                        <label className="jsa-label">생년월일 <span className="jsa-required">*</span></label>
                                        <div className="jsa-birthdate-row">
                                            <BirthDateSelect
                                                value={birthYear}
                                                onChange={(v) => { setBirthYear(v); setBirthDay(""); }}
                                                placeholder="년도"
                                                options={Array.from({ length: 66 }, (_, i) => 2010 - i).map((y) => ({ value: y, label: `${y}년` }))}
                                            />
                                            <BirthDateSelect
                                                value={birthMonth}
                                                onChange={(v) => { setBirthMonth(v); setBirthDay(""); }}
                                                placeholder="월"
                                                options={Array.from({ length: 12 }, (_, i) => i + 1).map((m) => ({ value: m, label: `${m}월` }))}
                                            />
                                            <BirthDateSelect
                                                value={birthDay}
                                                onChange={setBirthDay}
                                                placeholder="일"
                                                options={Array.from(
                                                    { length: birthYear && birthMonth ? new Date(Number(birthYear), Number(birthMonth), 0).getDate() : 31 },
                                                    (_, i) => i + 1
                                                ).map((d) => ({ value: d, label: `${d}일` }))}
                                            />
                                        </div>
                                    </div>
                                    <div className="jsa-field">
                                        <label className="jsa-label">성별 <span className="jsa-required">*</span></label>
                                        <div className="jsa-radio-group">
                                            {[{ value: "M", label: "남성" }, { value: "F", label: "여성" }].map((opt) => (
                                                <label key={opt.value} className={`jsa-radio-label ${form.gender === opt.value ? "active" : ""}`}>
                                                    <input type="radio" name="gender" value={opt.value} checked={form.gender === opt.value} onChange={handleChange} />
                                                    {opt.label}
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div className="jsa-field">
                                    <label className="jsa-label">이메일</label>
                                    <div className="jsa-email-container">
                                        <input
                                            className="jsa-input jsa-email-local"
                                            type="text"
                                            inputMode="email"
                                            value={emailLocal}
                                            onChange={handleEmailLocalChange}
                                            placeholder="이메일 아이디"
                                        />
                                        <div className="jsa-email-domain-row">
                                            <span className="jsa-email-at">@</span>
                                            <div className="jsa-email-domain-select-wrap">
                                                <SelectDropdown
                                                    name="emailDomain"
                                                    value={emailDomain}
                                                    onChange={handleEmailDomainSelect}
                                                    options={EMAIL_DOMAINS}
                                                    placeholder="도메인 선택"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    {emailDomain === "custom" && (
                                        <input
                                            className="jsa-input"
                                            type="text"
                                            value={emailDomainCustom}
                                            onChange={handleEmailDomainCustomChange}
                                            placeholder="도메인 직접 입력 (예: company.com)"
                                            style={{ marginTop: "8px" }}
                                        />
                                    )}
                                    {form.email && (
                                        <div className="jsa-email-preview">✉ {form.email}</div>
                                    )}
                                </div>

                                {/* 주소 */}
                                <div className="jsa-section-title" style={{ marginTop: "24px" }}>주소</div>

                                <div className="jsa-field">
                                    <label className="jsa-label">주소 <span className="jsa-required">*</span></label>
                                    <div className="jsa-address-row">
                                        <input className="jsa-input jsa-address-input" type="text" value={form.roadAddress} readOnly placeholder="주소 검색을 클릭하세요" />
                                        <button type="button" className="jsa-address-btn" onClick={handleAddressSearch}>주소 검색</button>
                                    </div>
                                    {form.zipcode && <span className="jsa-zipcode">{form.zipcode}</span>}
                                </div>

                                <div className="jsa-field">
                                    <label className="jsa-label">상세주소</label>
                                    <input className="jsa-input" type="text" name="detailAddress" value={form.detailAddress} onChange={handleChange} placeholder="상세주소를 입력하세요" />
                                </div>

                                {/* 경력 */}
                                <div className="jsa-section-title" style={{ marginTop: "24px" }}>경력</div>

                                <div className="jsa-field">
                                    <label className="jsa-label">경력 <span className="jsa-required">*</span></label>
                                    <SelectDropdown
                                        name="jobExperienceLevel"
                                        value={form.jobExperienceLevel}
                                        onChange={handleChange}
                                        options={EXPERIENCE_OPTIONS}
                                    />
                                </div>

                                {/* 경력사항 */}
                                <div className="jsa-career-header" style={{ marginTop: "24px" }}>
                                    <div className="jsa-section-title jsa-section-title--inline" style={{ marginTop: 0 }}>경력사항</div>
                                    <button type="button" className="jsa-career-add-btn" onClick={addCareer}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                                        </svg>
                                        경력 추가
                                    </button>
                                </div>

                                {careerHistories.length === 0 && (
                                    <div className="jsa-career-empty">
                                        <span>+ 경력 추가 버튼을 눌러 경력사항을 입력해주세요.</span>
                                    </div>
                                )}

                                <div className="jsa-career-list">
                                    {careerHistories.map((career, idx) => (
                                        <div key={idx} className="jsa-career-item">
                                            <div className="jsa-career-item-header">
                                                <span className="jsa-career-item-num">경력 {idx + 1}</span>
                                                <button type="button" className="jsa-career-remove-btn" onClick={() => removeCareer(idx)}>
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                                                    </svg>
                                                    삭제
                                                </button>
                                            </div>

                                            {planner ? (
                                            <PlannerCareerFields career={career} idx={idx} onUpdate={updateCareer} />
                                            ) : (
                                            <div className="jsa-career-fields">
                                                {/* 회사명 / 재직형태 */}
                                                <div className="jsa-row-2">
                                                    <div className="jsa-field">
                                                        <label className="jsa-label">회사명</label>
                                                        <input
                                                            className="jsa-input"
                                                            type="text"
                                                            placeholder="회사명을 입력하세요"
                                                            value={career.companyName}
                                                            onChange={(e) => updateCareer(idx, "companyName", e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="jsa-field">
                                                        <label className="jsa-label">재직형태</label>
                                                        <SelectDropdown
                                                            name={`employmentType-${idx}`}
                                                            value={career.employmentType}
                                                            onChange={(e) => updateCareer(idx, "employmentType", e.target.value)}
                                                            options={EMPLOYMENT_TYPE_OPTIONS}
                                                            placeholder="선택하세요"
                                                        />
                                                    </div>
                                                </div>

                                                {/* 직무 / 직책 */}
                                                <div className="jsa-row-2">
                                                    <div className="jsa-field">
                                                        <label className="jsa-label">직무</label>
                                                        <input
                                                            className="jsa-input"
                                                            type="text"
                                                            placeholder="직무를 입력하세요"
                                                            value={career.jobRole}
                                                            onChange={(e) => updateCareer(idx, "jobRole", e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="jsa-field">
                                                        <label className="jsa-label">직책</label>
                                                        <input
                                                            className="jsa-input"
                                                            type="text"
                                                            placeholder="직책을 입력하세요"
                                                            value={career.position}
                                                            onChange={(e) => updateCareer(idx, "position", e.target.value)}
                                                        />
                                                    </div>
                                                </div>

                                                {/* 재직기간 */}
                                                <div className="jsa-field">
                                                    <label className="jsa-label">재직기간</label>
                                                    <div className="jsa-career-period">
                                                        <input
                                                            className="jsa-input"
                                                            type="date"
                                                            value={career.startDate}
                                                            onChange={(e) => updateCareer(idx, "startDate", e.target.value)}
                                                        />
                                                        <span className="jsa-career-period-sep">~</span>
                                                        <input
                                                            className="jsa-input"
                                                            type="date"
                                                            value={career.endDate}
                                                            onChange={(e) => updateCareer(idx, "endDate", e.target.value)}
                                                            disabled={career.isCurrent}
                                                        />
                                                        <label className="jsa-career-current-label">
                                                            <input
                                                                type="checkbox"
                                                                checked={career.isCurrent}
                                                                onChange={(e) => updateCareer(idx, "isCurrent", e.target.checked)}
                                                            />
                                                            현재 재직 중
                                                        </label>
                                                    </div>
                                                </div>

                                                {/* 주요 업무내용 */}
                                                <div className="jsa-field">
                                                    <label className="jsa-label">주요 업무내용</label>
                                                    <textarea
                                                        className="jsa-textarea"
                                                        placeholder="담당했던 주요 업무를 입력해주세요."
                                                        value={career.mainDuties}
                                                        onChange={(e) => updateCareer(idx, "mainDuties", e.target.value)}
                                                        rows={3}
                                                        maxLength={1000}
                                                    />
                                                    <span className="jsa-char-count">{career.mainDuties.length} / 1000</span>
                                                </div>
                                            </div>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {/* 희망직종 */}
                                <div className="jsa-section-title" style={{ marginTop: "24px" }}>희망직종 <span className="jsa-required">*</span></div>

                                <div className="jsa-checkbox-group">
                                    {(fixedCategoryCode
                                        ? jobCategoryOptions.filter((o) => o.value === fixedCategoryCode)
                                        : jobCategoryOptions.filter((o) => o.value !== PLANNER_CATEGORY_CODE)
                                    ).map((opt) => (
                                        <label
                                            key={opt.value}
                                            className={`jsa-checkbox-label ${form.jobCategoryCodes.includes(opt.value) ? "active" : ""} ${fixedCategoryCode ? "jsa-checkbox-label--fixed" : ""}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={form.jobCategoryCodes.includes(opt.value)}
                                                onChange={() => !fixedCategoryCode && handleJobCategoryChange(opt.value)}
                                                readOnly={!!fixedCategoryCode}
                                            />
                                            {opt.label}
                                            {fixedCategoryCode && <span className="jsa-fixed-badge">고정</span>}
                                        </label>
                                    ))}
                                </div>



                                {/* e클린서비스 파일 업로드 */}
                                <div className="jsa-section-title-row" style={{ marginTop: "24px" }}>
                                    <span className="jsa-section-title jsa-section-title--inline">e클린서비스 파일 업로드</span>
                                    <a
                                        href="https://www.e-cleanins.or.kr/#"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="jsa-eclean-link"
                                    >
                                        e클린서비스 바로가기
                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                            <polyline points="15 3 21 3 21 9" />
                                            <line x1="10" y1="14" x2="21" y2="3" />
                                        </svg>
                                    </a>
                                </div>

                                <div className="jsa-upload-notice">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 3 }}>
                                        <circle cx="12" cy="12" r="10" />
                                        <line x1="12" y1="8" x2="12" y2="12" />
                                        <line x1="12" y1="16" x2="12.01" y2="16" />
                                    </svg>
                                    <span>필수 항목은 아니지만, <strong>e클린서비스 파일을 함께 제출하시면 담당자 검토 시 우선 안내</strong>를 받으실 수 있어 채용 기회가 높아집니다.</span>
                                </div>

                                <div className="jsa-upload-area">
                                    <label className="jsa-upload-label" htmlFor="resume-upload">
                                        {resumeFile ? (
                                            <span className="jsa-upload-filename">📎 {resumeFile.name}</span>
                                        ) : (
                                            <>
                                                <span className="jsa-upload-icon">⬆</span>
                                                <span>e클린서비스 파일을 선택하세요</span>
                                                <span className="jsa-upload-hint">PDF, DOC, DOCX, JPG, PNG (최대 10MB)</span>
                                            </>
                                        )}
                                    </label>
                                    <input
                                        id="resume-upload"
                                        type="file"
                                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                        onChange={handleResumeChange}
                                        style={{ display: "none" }}
                                    />
                                </div>

                                {/* 개인정보 동의 */}
                                <div className="jsa-privacy-row">
                                    <label className="jsa-privacy-check">
                                        <input type="checkbox" name="privacyAgreed" checked={form.privacyAgreed} onChange={handleChange} />
                                        <span>
                                            개인정보 수집·이용에 동의합니다.{" "}
                                            <button type="button" className="jsa-terms-link" onClick={() => setShowTermsModal(true)}>
                                                내용보기
                                            </button>
                                        </span>
                                    </label>
                                </div>

                                {/* 에러 메시지 */}
                                {error && <div className="jsa-error">{error}</div>}

                                {/* 제출 */}
                                <button type="submit" className="jsa-submit-btn" disabled={loading}>
                                    {loadingStep === 'uploading' ? "파일 업로드 중..." :
                                     loadingStep === 'submitting' ? "신청 중..." : "신청하기"}
                                </button>

                            </form>
                        </div>
                    </div>
                  )}
                </div>
            </div>

            {/* 개인정보처리방침 모달 */}
            {showTermsModal && (
                <div className="jsa-modal-overlay" onClick={() => setShowTermsModal(false)}>
                    <div className="jsa-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="jsa-modal-header">
                            <h3>{privacyTerms?.title || "개인정보처리방침"}</h3>
                            <button className="jsa-modal-close" onClick={() => setShowTermsModal(false)}>✕</button>
                        </div>
                        <div className="jsa-modal-body">
                            {privacyTerms?.content
                                ? privacyTerms.content.split("\n").map((line, i) => <p key={i}>{line || <br />}</p>)
                                : <p>개인정보처리방침을 불러오는 중입니다.</p>
                            }
                        </div>
                        <div className="jsa-modal-footer">
                            <button className="jsa-submit-btn" onClick={() => { setForm((p) => ({ ...p, privacyAgreed: true })); setShowTermsModal(false); }}>
                                동의하고 닫기
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Footer />

            <style>{`
                .jsa-page {
                    padding-top: 80px;
                    min-height: 100vh;
                    background: #f7f4f1;
                }

                /* ── 헤더 ── */
                .jsa-hero {
                    background: linear-gradient(135deg, #fd7314 0%, #f1863f 100%);
                    padding: 48px 20px;
                    text-align: center;
                }
                .jsa-hero-inner { max-width: 700px; margin: 0 auto; }
                .jsa-hero-kicker {
                    font-size: 12px;
                    letter-spacing: 0.2em;
                    color: rgba(255,255,255,0.8);
                    font-weight: 600;
                    margin: 0 0 8px;
                }
                .jsa-hero-title {
                    font-size: 2rem;
                    font-weight: 800;
                    color: #fff;
                    margin: 0 0 10px;
                }
                .jsa-hero-sub {
                    font-size: 1rem;
                    color: rgba(255,255,255,0.85);
                    margin: 0;
                }

                /* ── 본문 레이아웃 ── */
                .jsa-body {
                    max-width: 1100px;
                    margin: 0 auto;
                    padding: 48px 20px 80px;
                }
                .jsa-layout {
                    display: grid;
                    grid-template-columns: 340px 1fr;
                    gap: 40px;
                    align-items: start;
                }

                /* ── 이미지 컬럼 ── */
                .jsa-image-col {
                    position: sticky;
                    top: 104px;
                }
                .jsa-visual-img {
                    width: 100%;
                    height: 420px;
                    object-fit: cover;
                    border-radius: 16px;
                    display: block;
                    background: #e8ddd4;
                }
                .jsa-image-badge {
                    margin-top: 16px;
                    background: #fff;
                    border-radius: 10px;
                    padding: 16px 20px;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.07);
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }
                .jsa-badge-num {
                    font-size: 1.1rem;
                    font-weight: 800;
                    color: #f16102;
                }
                .jsa-badge-txt {
                    font-size: 0.85rem;
                    color: #888;
                }

                /* ── 폼 카드 ── */
                .jsa-form-col {
                    background: #fff;
                    border-radius: 16px;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.07);
                    padding: 36px 32px;
                }
                .jsa-form { display: flex; flex-direction: column; gap: 14px; }

                .jsa-section-title {
                    font-size: 0.8rem;
                    font-weight: 600;
                    letter-spacing: 0.08em;
                    text-transform: uppercase;
                    color: #f16102;
                    padding-bottom: 8px;
                    border-bottom: 1.5px solid #fde8d8;
                    margin-bottom: 2px;
                    text-align: left;
                    align-self: flex-start;
                    width: 100%;
                }

                /* 섹션 제목 + 바로가기 링크(e클린서비스)를 한 줄에 배치 */
                .jsa-section-title-row {
                    display: flex;
                    align-items: flex-end;
                    justify-content: space-between;
                    gap: 10px;
                    flex-wrap: wrap;
                    padding-bottom: 8px;
                    border-bottom: 1.5px solid #fde8d8;
                    margin-bottom: 2px;
                    width: 100%;
                }
                .jsa-section-title--inline {
                    padding-bottom: 0;
                    margin-bottom: 0;
                    border-bottom: none;
                    width: auto;
                    align-self: auto;
                }
                .jsa-eclean-link {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    font-size: 0.82rem;
                    font-weight: 600;
                    color: #fff;
                    text-decoration: none;
                    white-space: nowrap;
                    flex-shrink: 0;
                    padding: 7px 14px;
                    background: #f16102;
                    border: none;
                    border-radius: 8px;
                    transition: background 0.2s;
                }
                .jsa-eclean-link:hover,
                .jsa-eclean-link:focus-visible {
                    background: #d95502;
                }
                .jsa-eclean-link svg { flex-shrink: 0; }

                .jsa-row-2 {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 14px;
                }
                .jsa-field { display: flex; flex-direction: column; gap: 6px; }
                .jsa-email-container { display: flex; align-items: center; gap: 8px; }
                .jsa-email-local { flex: 1; min-width: 0; }
                .jsa-email-domain-row { display: flex; align-items: center; gap: 6px; flex: 1; min-width: 0; }
                .jsa-email-at { font-size: 1rem; font-weight: 600; color: #555; flex-shrink: 0; }
                .jsa-email-domain-select-wrap { flex: 1; min-width: 0; }
                .jsa-email-preview { margin-top: 4px; font-size: 0.8rem; color: #f16102; font-weight: 500; }
                .jsa-birthdate-row { display: grid; grid-template-columns: 2fr 1.2fr 1.2fr; gap: 8px; }
                .bds-wrap { position: relative; width: 100%; }
                .bds-trigger {
                    width: 100%; display: flex; align-items: center; justify-content: space-between;
                    border: 1.5px solid #e5ddd7; border-radius: 8px; padding: 10px 14px;
                    font-size: 0.9rem; color: #333; background: #fff; cursor: pointer;
                    font-family: inherit; transition: border-color 0.2s; text-align: left;
                }
                .bds-trigger--open, .bds-trigger:focus { border-color: #f16102; outline: none; }
                .bds-placeholder { color: #aaa; }
                .bds-arrow { font-size: 0.85rem; color: #666; transition: transform 0.2s; flex-shrink: 0; line-height: 1; }
                .bds-trigger--open .bds-arrow { transform: rotate(180deg); }
                .bds-list {
                    position: absolute; top: calc(100% + 4px); left: 0; right: 0; z-index: 200;
                    background: #fff; border: 1.5px solid #f16102; border-radius: 8px;
                    max-height: 200px; overflow-y: auto; margin: 0; padding: 4px 0;
                    list-style: none; box-shadow: 0 6px 20px rgba(0,0,0,0.12);
                }
                .bds-list::-webkit-scrollbar { width: 6px; }
                .bds-list::-webkit-scrollbar-track { background: transparent; }
                .bds-list::-webkit-scrollbar-thumb { background: #e5ddd7; border-radius: 3px; }
                .bds-item {
                    padding: 9px 14px; font-size: 0.9rem; color: #333; cursor: pointer;
                    transition: background 0.15s;
                }
                .bds-item:hover { background: #fff4ec; color: #f16102; }
                .bds-item--selected { background: #fff4ec; color: #f16102; font-weight: 600; }
                .jsa-label { font-size: 0.85rem; font-weight: 600; color: #444; text-align: left; }
                .jsa-required { color: #f16102; }

                .jsa-input, .jsa-select, .jsa-textarea {
                    border: 1.5px solid #e5ddd7;
                    border-radius: 8px;
                    padding: 10px 14px;
                    font-size: 0.9rem;
                    color: #333;
                    background: #fff;
                    outline: none;
                    transition: border-color 0.2s;
                    font-family: inherit;
                }
                .jsa-input:focus, .jsa-select:focus, .jsa-textarea:focus {
                    border-color: #f16102;
                }

                /* ── 커스텀 셀렉트 ── */
                .jsa-custom-select {
                    position: relative;
                    width: 100%;
                }
                .jsa-custom-select-trigger {
                    width: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 8px;
                    padding: 10px 14px;
                    border: 1.5px solid #e5ddd7;
                    border-radius: 8px;
                    background: #fff;
                    font-size: 0.9rem;
                    color: #aaa;
                    font-family: inherit;
                    cursor: pointer;
                    text-align: left;
                    transition: border-color 0.2s;
                }
                .jsa-custom-select-trigger.has-value { color: #333; }
                .jsa-custom-select-trigger.open,
                .jsa-custom-select-trigger:focus {
                    border-color: #f16102;
                    outline: none;
                }
                .jsa-select-arrow {
                    flex-shrink: 0;
                    color: #f16102;
                    transition: transform 0.2s ease;
                }
                .jsa-select-arrow.open { transform: rotate(180deg); }

                .jsa-custom-select-list {
                    position: absolute;
                    top: calc(100% + 4px);
                    left: 0;
                    right: 0;
                    background: #fff;
                    border: 1.5px solid #f16102;
                    border-radius: 10px;
                    box-shadow: 0 8px 24px rgba(0,0,0,0.1);
                    padding: 6px;
                    margin: 0;
                    list-style: none;
                    z-index: 100;
                    overflow: hidden;
                }
                .jsa-custom-select-item {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 14px;
                    font-size: 0.88rem;
                    color: #444;
                    border-radius: 7px;
                    cursor: pointer;
                    transition: background 0.15s, color 0.15s;
                }
                .jsa-custom-select-item:hover {
                    background: #fff8f4;
                    color: #f16102;
                }
                .jsa-custom-select-item.selected {
                    background: #fff3eb;
                    color: #f16102;
                    font-weight: 600;
                }
                .jsa-custom-select-item.selected svg { flex-shrink: 0; }
                .jsa-textarea { resize: vertical; min-height: 100px; }

                /* 주소 */
                .jsa-address-row { display: flex; gap: 8px; }
                .jsa-address-input { flex: 1; background: #faf7f4; cursor: default; }
                .jsa-address-btn {
                    white-space: nowrap;
                    padding: 10px 16px;
                    background: #f16102;
                    color: #fff;
                    border: none;
                    border-radius: 8px;
                    font-size: 0.85rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: background 0.2s;
                }
                .jsa-address-btn:hover { background: #d95502; }
                .jsa-zipcode { font-size: 0.8rem; color: #888; margin-top: 2px; }

                /* 라디오 */
                .jsa-radio-group { display: flex; gap: 10px; }
                .jsa-radio-label {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    padding: 9px 18px;
                    border: 1.5px solid #e5ddd7;
                    border-radius: 8px;
                    font-size: 0.88rem;
                    font-weight: 600;
                    color: #666;
                    cursor: pointer;
                    transition: all 0.15s;
                    flex: 1;
                    justify-content: center;
                }
                .jsa-radio-label input { display: none; }
                .jsa-radio-label.active {
                    border-color: #f16102;
                    color: #f16102;
                    background: #fff8f4;
                }

                /* 체크박스 (희망직종) */
                .jsa-checkbox-group { display: flex; gap: 10px; flex-wrap: wrap; }
                .jsa-checkbox-label {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    padding: 9px 20px;
                    border: 1.5px solid #e5ddd7;
                    border-radius: 8px;
                    font-size: 0.88rem;
                    font-weight: 600;
                    color: #666;
                    cursor: pointer;
                    transition: all 0.15s;
                }
                .jsa-checkbox-label input { display: none; }
                .jsa-checkbox-label.active {
                    border-color: #f16102;
                    color: #f16102;
                    background: #fff8f4;
                }
                .jsa-checkbox-label--fixed { cursor: default; }
                .jsa-fixed-badge {
                    margin-left: 6px; font-size: 0.7rem; font-weight: 700;
                    background: #f16102; color: #fff;
                    padding: 1px 6px; border-radius: 6px; vertical-align: middle;
                }

                /* e클린서비스 파일 업로드 안내 */
                .jsa-upload-notice {
                    display: flex;
                    align-items: flex-start;
                    gap: 5px;
                    text-align: left;
                    background: #fff8f4;
                    border: 1px solid #fde0c8;
                    border-radius: 8px;
                    padding: 10px 14px;
                    font-size: 0.82rem;
                    color: #888;
                    line-height: 1.6;
                }
                .jsa-upload-notice strong {
                    color: #f16102;
                    font-weight: 600;
                }
                .jsa-upload-notice svg {
                    color: #f16102;
                    margin-top: 2px;
                }

                /* 파일 업로드 */
                .jsa-upload-area {
                    border: 2px dashed #e5ddd7;
                    border-radius: 10px;
                    background: #faf7f4;
                    transition: border-color 0.2s;
                }
                .jsa-upload-area:hover { border-color: #f16102; }
                .jsa-upload-label {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    padding: 24px;
                    cursor: pointer;
                    font-size: 0.88rem;
                    color: #888;
                    text-align: center;
                }
                .jsa-upload-icon { font-size: 1.4rem; color: #f16102; }
                .jsa-upload-hint { font-size: 0.75rem; color: #bbb; }
                .jsa-upload-filename { font-size: 0.88rem; color: #f16102; font-weight: 600; }

                /* 개인정보 동의 */
                .jsa-privacy-row { margin-top: 8px; }
                .jsa-privacy-check {
                    display: flex;
                    align-items: flex-start;
                    gap: 8px;
                    font-size: 0.85rem;
                    color: #555;
                    cursor: pointer;
                }
                .jsa-privacy-check input { margin-top: 2px; accent-color: #f16102; flex-shrink: 0; }
                .jsa-terms-link {
                    background: none;
                    border: none;
                    color: #f16102;
                    font-size: 0.85rem;
                    font-weight: 600;
                    cursor: pointer;
                    padding: 0;
                    text-decoration: underline;
                }

                /* 에러 / 제출 */
                .jsa-error {
                    background: #fff2f2;
                    border: 1px solid #ffc5c5;
                    border-radius: 8px;
                    padding: 12px 16px;
                    font-size: 0.85rem;
                    color: #d32f2f;
                }
                .jsa-submit-btn {
                    margin-top: 8px;
                    width: 100%;
                    padding: 14px;
                    background: #f16102;
                    color: #fff;
                    border: none;
                    border-radius: 10px;
                    font-size: 1rem;
                    font-weight: 700;
                    cursor: pointer;
                    transition: background 0.2s, opacity 0.2s;
                }
                .jsa-submit-btn:hover:not(:disabled) { background: #d95502; }
                .jsa-submit-btn:disabled { opacity: 0.6; cursor: not-allowed; }

                /* ── 성공 화면 ── */
                .jsa-success-card {
                    max-width: 600px;
                    margin: 0 auto;
                    background: #fff;
                    border-radius: 24px;
                    box-shadow: 0 8px 40px rgba(0,0,0,0.10);
                    padding: 56px 44px 48px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    text-align: center;
                    gap: 28px;
                    animation: jsa-card-in 0.4s ease both;
                }
                @keyframes jsa-card-in {
                    from { opacity: 0; transform: translateY(16px); }
                    to   { opacity: 1; transform: translateY(0); }
                }

                /* 아이콘 뱃지 */
                .jsa-success-badge {
                    position: relative;
                    width: 96px;
                    height: 96px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .jsa-success-badge-ring {
                    position: absolute;
                    inset: 0;
                    border-radius: 50%;
                    background: #fff5ee;
                    animation: jsa-ring-pulse 2s ease-in-out 0.8s infinite;
                }
                @keyframes jsa-ring-pulse {
                    0%, 100% { transform: scale(1);   opacity: 1; }
                    50%       { transform: scale(1.12); opacity: 0.6; }
                }
                .jsa-success-svg {
                    position: relative;
                    width: 72px;
                    height: 72px;
                }
                .jsa-success-circle {
                    stroke-dasharray: 145;
                    stroke-dashoffset: 145;
                    animation: jsa-draw-circle 0.6s cubic-bezier(.4,0,.2,1) forwards;
                }
                .jsa-success-check {
                    stroke-dasharray: 48;
                    stroke-dashoffset: 48;
                    animation: jsa-draw-check 0.35s ease 0.5s forwards;
                }
                @keyframes jsa-draw-circle { to { stroke-dashoffset: 0; } }
                @keyframes jsa-draw-check  { to { stroke-dashoffset: 0; } }

                /* 제목 영역 */
                .jsa-success-header { display: flex; flex-direction: column; gap: 10px; }
                .jsa-success-title {
                    font-size: 1.55rem;
                    font-weight: 800;
                    color: #1c1814;
                    margin: 0;
                    line-height: 1.35;
                }
                .jsa-success-desc {
                    font-size: 0.93rem;
                    color: #888;
                    line-height: 1.75;
                    margin: 0;
                }

                /* 접수 요약 */
                .jsa-success-summary {
                    width: 100%;
                    background: #faf7f4;
                    border-radius: 14px;
                    padding: 20px 24px;
                    text-align: left;
                }
                .jsa-summary-title {
                    font-size: 0.72rem;
                    font-weight: 700;
                    letter-spacing: 0.1em;
                    text-transform: uppercase;
                    color: #f16102;
                    margin: 0 0 14px;
                }
                .jsa-summary-list {
                    list-style: none;
                    margin: 0;
                    padding: 0;
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                }
                .jsa-summary-item {
                    display: flex;
                    align-items: baseline;
                    gap: 12px;
                    font-size: 0.88rem;
                }
                .jsa-summary-key {
                    flex-shrink: 0;
                    width: 52px;
                    color: #aaa;
                    font-weight: 600;
                    font-size: 0.82rem;
                }
                .jsa-summary-val {
                    color: #333;
                    font-weight: 500;
                }

                /* 진행 단계 */
                .jsa-success-steps {
                    display: flex;
                    align-items: flex-start;
                    width: 100%;
                    justify-content: center;
                }
                .jsa-step {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 10px;
                    flex: 1;
                    max-width: 110px;
                }
                .jsa-step-circle {
                    width: 38px;
                    height: 38px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 0.85rem;
                    font-weight: 700;
                    transition: all 0.2s;
                }
                .jsa-step--done .jsa-step-circle {
                    background: #f16102;
                    color: #fff;
                    box-shadow: 0 4px 12px rgba(241,97,2,0.35);
                }
                .jsa-step--pending .jsa-step-circle {
                    background: #f0ebe6;
                    color: #c5b9b0;
                }
                .jsa-step-label {
                    font-size: 0.76rem;
                    font-weight: 600;
                    color: #bbb;
                    white-space: nowrap;
                }
                .jsa-step--done .jsa-step-label {
                    color: #f16102;
                }
                .jsa-step-line {
                    flex: 1;
                    height: 2px;
                    background: linear-gradient(to right, #f16102 0%, #f0ebe6 100%);
                    margin-top: 18px;
                    min-width: 24px;
                    border-radius: 2px;
                }

                /* 안내 박스 */
                .jsa-success-notice {
                    display: flex;
                    align-items: flex-start;
                    gap: 10px;
                    background: #fff8f4;
                    border: 1px solid #fde0c8;
                    border-radius: 12px;
                    padding: 14px 18px;
                    font-size: 0.83rem;
                    color: #999;
                    line-height: 1.65;
                    text-align: left;
                    width: 100%;
                }

                /* 버튼 그룹 */
                .jsa-success-actions {
                    display: flex;
                    gap: 12px;
                    width: 100%;
                }
                .jsa-success-home-btn {
                    flex: 1;
                    padding: 14px;
                    border: 1.5px solid #e5ddd7;
                    border-radius: 10px;
                    font-size: 0.92rem;
                    font-weight: 700;
                    color: #666;
                    background: #fff;
                    text-decoration: none;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    transition: border-color 0.2s, color 0.2s, background 0.2s;
                }
                .jsa-success-home-btn:hover {
                    border-color: #f16102;
                    color: #f16102;
                    background: #fff8f4;
                }
                .jsa-success-retry-btn {
                    flex: 1;
                    margin-top: 0;
                }

                @media (max-width: 600px) {
                    .jsa-success-card { padding: 40px 24px 36px; gap: 22px; }
                    .jsa-success-title { font-size: 1.3rem; }
                    .jsa-success-actions { flex-direction: column; }
                    .jsa-summary-key { width: 44px; }
                }

                /* 모달 */
                .jsa-modal-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(0,0,0,0.5);
                    z-index: 2000;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 20px;
                }
                .jsa-modal {
                    background: #fff;
                    border-radius: 14px;
                    width: 100%;
                    max-width: 540px;
                    max-height: 80vh;
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                }
                .jsa-modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 20px 24px;
                    border-bottom: 1px solid #f0ebe6;
                }
                .jsa-modal-header h3 { margin: 0; font-size: 1rem; font-weight: 700; }
                .jsa-modal-close {
                    background: none;
                    border: none;
                    font-size: 1.1rem;
                    cursor: pointer;
                    color: #888;
                    padding: 4px;
                }
                .jsa-modal-body {
                    flex: 1;
                    overflow-y: auto;
                    padding: 20px 24px;
                    font-size: 0.85rem;
                    color: #555;
                    line-height: 1.8;
                    text-align: left;
                }
                .jsa-modal-body p { margin: 0 0 4px; }
                .jsa-modal-footer {
                    padding: 16px 24px;
                    border-top: 1px solid #f0ebe6;
                }
                .jsa-modal-footer .jsa-submit-btn { margin-top: 0; }

                /* ── 경력사항 ── */
                .jsa-career-header {
                    display: flex;
                    align-items: flex-end;
                    justify-content: space-between;
                    gap: 10px;
                    flex-wrap: wrap;
                    padding-bottom: 8px;
                    border-bottom: 1.5px solid #fde8d8;
                    margin-bottom: 14px;
                    width: 100%;
                }
                .jsa-career-add-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 7px 14px;
                    background: #f16102;
                    color: #fff;
                    border: none;
                    border-radius: 8px;
                    font-size: 0.82rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: background 0.2s;
                    white-space: nowrap;
                    flex-shrink: 0;
                }
                .jsa-career-add-btn:hover { background: #d95502; }
                .jsa-career-empty {
                    width: 100%;
                    padding: 16px;
                    background: #faf7f4;
                    border: 1.5px dashed #e5ddd7;
                    border-radius: 10px;
                    text-align: center;
                    font-size: 0.84rem;
                    color: #aaa;
                }
                .jsa-career-list {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                    width: 100%;
                }
                .jsa-career-item {
                    border: 1.5px solid #e5ddd7;
                    border-radius: 12px;
                    overflow: hidden;
                }
                .jsa-career-item-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 10px 16px;
                    background: #faf7f4;
                    border-bottom: 1.5px solid #e5ddd7;
                }
                .jsa-career-item-num {
                    font-size: 0.82rem;
                    font-weight: 700;
                    color: #f16102;
                }
                .jsa-career-remove-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    padding: 4px 10px;
                    background: none;
                    border: 1.5px solid #e5ddd7;
                    border-radius: 6px;
                    font-size: 0.78rem;
                    font-weight: 600;
                    color: #888;
                    cursor: pointer;
                    transition: all 0.15s;
                }
                .jsa-career-remove-btn:hover {
                    border-color: #f16102;
                    color: #f16102;
                    background: #fff4ec;
                }
                .jsa-career-fields {
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                    padding: 16px;
                }
                .jsa-career-period {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    flex-wrap: wrap;
                }
                .jsa-career-period .jsa-input { flex: 1; min-width: 120px; }
                .jsa-career-period-sep {
                    font-size: 0.9rem;
                    color: #888;
                    flex-shrink: 0;
                }
                .jsa-career-current-label {
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                    font-size: 0.82rem;
                    font-weight: 600;
                    color: #555;
                    cursor: pointer;
                    white-space: nowrap;
                    flex-shrink: 0;
                }
                .jsa-career-current-label input[type="checkbox"] {
                    accent-color: #f16102;
                    width: 15px;
                    height: 15px;
                    cursor: pointer;
                }
                .jsa-char-count {
                    font-size: 0.78rem;
                    color: #aaa;
                    text-align: right;
                    margin-top: 2px;
                }
                .jsa-input:disabled {
                    background: #f2f0ee;
                    color: #bbb;
                    cursor: not-allowed;
                }

                /* ── 반응형 ── */
                @media (max-width: 768px) {
                    .jsa-layout {
                        grid-template-columns: 1fr;
                    }
                    .jsa-image-col { display: none; }
                    .jsa-body { padding: 24px 16px 48px; }
                    .jsa-form-col { padding: 24px 16px; }
                    .jsa-row-2 { grid-template-columns: 1fr; }
                    .jsa-hero-title { font-size: 1.5rem; }
                    .jsa-hero { padding: 36px 16px; }
                    .jsa-birthdate-row { grid-template-columns: 1fr 1fr 1fr; gap: 6px; }
                    .jsa-email-container { flex-direction: column; align-items: stretch; gap: 8px; }
                    .jsa-email-local { width: 100%; }
                    .jsa-email-domain-row { width: 100%; }
                    .jsa-checkbox-group { gap: 8px; }
                    .jsa-checkbox-label { padding: 8px 14px; font-size: 0.84rem; }
                    .jsa-radio-group { gap: 8px; }
                    .jsa-radio-label { padding: 8px 12px; font-size: 0.84rem; }
                    .jsa-career-period { flex-direction: column; align-items: stretch; }
                    .jsa-career-period .jsa-input { min-width: unset; }
                    .jsa-career-period-sep { display: none; }
                    .jsa-career-current-label { justify-content: flex-start; }
                    .jsa-input, .jsa-select, .jsa-textarea,
                    .jsa-custom-select-trigger, .bds-trigger {
                        font-size: 16px;
                    }
                }

                @media (max-width: 480px) {
                    .jsa-body { padding: 16px 12px 40px; }
                    .jsa-form-col { padding: 20px 14px; border-radius: 12px; }
                    .jsa-hero { padding: 28px 12px; }
                    .jsa-hero-title { font-size: 1.25rem; }
                    .jsa-hero-sub { font-size: 0.88rem; }
                    .jsa-birthdate-row { grid-template-columns: 1fr; gap: 8px; }
                    .jsa-form { gap: 12px; }
                    .jsa-section-title { font-size: 0.75rem; }
                    .jsa-eclean-link { font-size: 0.74rem; padding: 5px 10px; }
                    .jsa-label { font-size: 0.82rem; }
                    .jsa-upload-label { padding: 18px 12px; font-size: 0.84rem; }
                    .jsa-submit-btn { font-size: 0.95rem; padding: 13px; }
                    .jsa-address-btn { font-size: 0.8rem; padding: 10px 12px; }
                }
            `}</style>
        </>
    );
}
